/**
 * Altar del Perfume — variant picker.
 *
 * Progressive enhancement over a form that already works without it: the option radios post
 * a real variant id, and a <noscript> select covers the scripting-disabled case. This element
 * resolves the chosen combination to a variant and keeps price, availability and the URL in
 * step, so nothing silently disagrees with what the customer is about to buy.
 *
 * El precio lo formatea `formatMoney` de Horizon, que replica el filtro `money` de Liquid y
 * conoce los diez formatos de moneda de Shopify. La versión anterior reemplazaba a mano sólo
 * `{{amount}}`, y como esta tienda usa `${{amount_with_comma_separator}}` no encontraba nada que
 * reemplazar: en cada perfume con más de un tamaño el cliente veía la plantilla en crudo donde
 * debía estar el precio. Los productos de una sola versión no lo mostraban porque ahí nunca se
 * reescribe el precio que ya imprimió Liquid.
 */
import { formatMoney } from '@theme/money-formatting';
class AltarVariants extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-altar-variant-data]');
    if (!data) return;

    try {
      this.variants = JSON.parse(data.textContent);
    } catch {
      return;
    }

    this.idInput = this.querySelector('[data-altar-variant-id]');
    this.priceHost = document.getElementById(this.dataset.priceTarget);
    this.submit = document.getElementById(this.dataset.submitTarget);
    this.radios = Array.from(this.querySelectorAll('[data-altar-option]'));

    this.onChange = () => this.resolve();
    this.radios.forEach((radio) => radio.addEventListener('change', this.onChange));
  }

  disconnectedCallback() {
    this.radios?.forEach((radio) => radio.removeEventListener('change', this.onChange));
  }

  get selection() {
    const chosen = [];
    this.radios.forEach((radio) => {
      if (radio.checked) chosen[Number(radio.dataset.altarOption)] = radio.value;
    });
    return chosen;
  }

  resolve() {
    const chosen = this.selection;
    const variant = this.variants.find((candidate) =>
      candidate.options.every((option, index) => option === chosen[index])
    );

    if (this.idInput) this.idInput.value = variant ? variant.id : '';

    if (this.submit) {
      const available = Boolean(variant && variant.available);
      this.submit.disabled = !available;
      const label = available ? this.submit.dataset.labelAvailable : this.submit.dataset.labelSoldOut;
      if (label) this.submit.textContent = label;
    }

    if (this.priceHost && variant) {
      this.priceHost.innerHTML = this.priceMarkup(variant);
    }

    if (variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  priceMarkup(variant) {
    const money = (cents) =>
      formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'COP');

    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    const current = `<span class="altar-pdp__price-now">${money(variant.price)}</span>`;
    if (!onSale) return current;

    return `<s class="altar-pdp__price-was">${money(variant.compare_at_price)}</s>${current}`;
  }
}

if (!customElements.get('altar-variants')) {
  customElements.define('altar-variants', AltarVariants);
}

/**
 * − y + alrededor del campo de cantidad. Sólo mueven el número; el campo sigue siendo el que
 * se envía, así que escribir a mano también vale. Nunca baja de `min` (1).
 */
class AltarQty extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    this.minus = this.querySelector('[data-step="-1"]');
    if (!this.input) return;

    this.onClick = (event) => {
      const button = event.target.closest('[data-step]');
      if (!button) return;
      const min = Number(this.input.min) || 1;
      const next = Math.max(min, (Number(this.input.value) || min) + Number(button.dataset.step));
      this.input.value = String(next);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.sync();
    };
    this.onInput = () => this.sync();

    this.addEventListener('click', this.onClick);
    this.input.addEventListener('input', this.onInput);
    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.input?.removeEventListener('input', this.onInput);
  }

  sync() {
    const min = Number(this.input.min) || 1;
    if (this.minus) this.minus.disabled = (Number(this.input.value) || min) <= min;
  }
}

if (!customElements.get('altar-qty')) {
  customElements.define('altar-qty', AltarQty);
}

/**
 * Descripción recortada con «Seguir indagando». Recorta sólo si el texto es más largo que el
 * recorte: un botón que no abre nada nuevo sería peor que no tenerlo.
 */
class AltarMore extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-more-body]');
    this.button = this.querySelector('button[aria-controls]');
    if (!this.body || !this.button) return;

    this.body.classList.add('is-clamped');
    // Medido después de aplicar el recorte: si nada quedó oculto, no hace falta el botón.
    if (this.body.scrollHeight <= this.body.clientHeight + 4) {
      this.body.classList.remove('is-clamped');
      return;
    }

    this.button.hidden = false;
    this.onClick = () => {
      const open = this.button.getAttribute('aria-expanded') !== 'true';
      this.button.setAttribute('aria-expanded', String(open));
      this.body.classList.toggle('is-clamped', !open);
      this.button.textContent = open ? this.button.dataset.labelLess : this.button.dataset.labelMore;
    };
    this.button.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }
}

if (!customElements.get('altar-more')) {
  customElements.define('altar-more', AltarMore);
}
